import React from 'react';
import { Table, Typography } from 'antd';

import { buildVariantLabel } from '../Orders/AssortmentModal';
import { formatNumber, formatMoney } from '../Orders/orderFormHelpers';
import { hasMultipleComponentPrices } from '../Orders/orderLineValue';

const { Text } = Typography;

// Same naming as the order PDF: product name followed by its variant's attribute values.
export const buildLineName = (line) =>
  `${line.product_name || ''} ${buildVariantLabel(line.variant)}`.trim() || '-';

// product_name_snapshot only stores the base product name, so the variant is
// what tells components apart.
const buildComponentName = (component) =>
  `${component.component_product?.name || component.product_name_snapshot || ''} ${buildVariantLabel(component.component_variant)}`.trim();

export const isAssorted = (line) => (line.components || []).length > 0;

// Same bridge the backend uses to value a component credit per stem.
export const componentStemsPerUnit = (unit, component) => {
  if (unit === 'STEM') return 1;
  if (unit === 'BUNCH') return Number(component.stems_per_bunch) || null;
  return null;
};

export const assortedStemsPerBillingUnit = (line) => {
  const stemsPerBox = line.components.reduce((sum, c) => sum + Number(c.bunches) * Number(c.stems_per_bunch), 0);
  const bunchesPerBox = line.components.reduce((sum, c) => sum + Number(c.bunches), 0);

  if (line.billing_unit === 'TALLO') return 1;
  if (line.billing_unit === 'RAMO') return bunchesPerBox ? stemsPerBox / bunchesPerBox : null;
  if (line.billing_unit === 'CAJA') return stemsPerBox || null;
  return null;
};

// Stems in one entered unit of a whole assortment: a box is all its
// components and a bunch, the box's average bunch (same bridge as the backend).
export const assortedStemsPerUnit = (unit, line) => {
  const stemsPerBox = line.components.reduce((sum, c) => sum + Number(c.bunches) * Number(c.stems_per_bunch), 0);
  const bunchesPerBox = line.components.reduce((sum, c) => sum + Number(c.bunches), 0);

  if (unit === 'STEM') return 1;
  if (unit === 'BUNCH') return bunchesPerBox ? stemsPerBox / bunchesPerBox : null;
  if (unit === 'BOX') return stemsPerBox || null;
  return null;
};

const BILLING_UNIT_SINGULAR = { TALLO: 'tallo', RAMO: 'ramo', CAJA: 'caja' };

// Precio de una línea de factura, como en los PDF: un surtido por tallo con
// precios distintos por componente dice "Múltiples"; si todos comparten precio,
// ese es el de la línea.
export const linePriceLabel = (line, currency) => {
  if (isAssorted(line) && line.billing_unit === 'TALLO' && hasMultipleComponentPrices(line.components, line.unit_price)) {
    return 'Múltiples';
  }
  return `${formatMoney(line.unit_price, currency)} / ${BILLING_UNIT_SINGULAR[line.billing_unit] || ''}`;
};

// Tallos que una baja le pide a un componente: los digitados en él (`own`) más
// su parte de lo digitado para la línea completa (`whole`), en proporción a sus
// tallos. Es la misma regla con la que valida el backend.
export const requestedComponentStems = ({ line, component, own, whole }) => {
  const ownStems = own?.quantity ? own.quantity * (componentStemsPerUnit(own.unit, component) || 0) : 0;
  const wholeStems = whole?.quantity ? whole.quantity * (assortedStemsPerUnit(whole.unit, line) || 0) : 0;
  return ownStems + (wholeStems * Number(component.invoiced_stems)) / Number(line.invoiced);
};

const toCents = (value) => Math.round(Number(value) * 100);

export const hasComponentPrice = (component) =>
  component?.unit_price !== null && component?.unit_price !== undefined;

// Un surtido por tallo con precios por componente (factura con valor
// congelado) también se puede dar de baja completo, en cajas o ramos.
export const allowsWholeAssortedCredit = (line) =>
  isAssorted(line) && line.billing_unit === 'TALLO' && line.line_amount !== null && line.line_amount !== undefined;

// Valor de una línea de la baja, con la misma regla del backend. `factor` lleva
// la cantidad digitada a la unidad de cobro de la línea.
//   - Componente con precio, por tallo: sus tallos × su precio.
//   - Surtido completo: su parte del valor congelado de la línea. Se redondea
//     lo acumulado con las bajas anteriores de la línea completa
//     (`whole_credited_stems`), así varias bajas parciales suman la línea exacta.
//   - Lo demás: cantidad × factor × precio de la línea.
export const creditValue = ({ line, component, whole, quantity, factor }) => {
  if (component && line.billing_unit === 'TALLO' && hasComponentPrice(component)) {
    return (quantity * factor * toCents(component.unit_price)) / 100;
  }
  if (whole) {
    const invoiced = Number(line.quantity) * Number(line.conversion_factor);
    const before = Number(line.whole_credited_stems) || 0;
    const cents = toCents(line.line_amount);
    return (Math.round((cents * (before + quantity * factor)) / invoiced) - Math.round((cents * before) / invoiced)) / 100;
  }
  return quantity * factor * Number(line.unit_price);
};

const formatCount = (value) => (Number(value) || 0).toLocaleString('es-CO');

// Recipe of an assorted line. Ramos y tallos son de toda la línea (`boxes`
// cajas), como en los PDF: así tallos × precio da el valor de cada fila. When
// the components carry their own price (a line billed by stem), each one also
// shows its price and value; otherwise the price belongs to the line and this
// table only shows quantities.
// `extraColumns` lets a screen append its own columns (e.g. credit inputs).
export const AssortmentBreakdown = ({ components, boxes = 1, extraColumns = [] }) => {
  const lineBunches = (record) => Number(boxes) * Number(record.bunches);
  const lineStems = (record) => lineBunches(record) * Number(record.stems_per_bunch);
  const valueCents = (record) => lineStems(record) * toCents(record.unit_price);

  const priced = components.some(hasComponentPrice);
  const priceColumns = priced ? [
    {
      title: 'Precio',
      key: 'unit_price',
      align: 'right',
      render: (_, record) => (hasComponentPrice(record) ? formatNumber(record.unit_price) : '-'),
    },
    {
      title: 'Valor',
      key: 'value',
      align: 'right',
      render: (_, record) => (hasComponentPrice(record) ? formatNumber(valueCents(record) / 100) : '-'),
    },
  ] : [];
  const baseColumns = [
    {
      title: 'Componente',
      key: 'name',
      render: (_, record) => buildComponentName(record),
    },
    {
      title: 'Ramos',
      key: 'bunches',
      align: 'right',
      render: (_, record) => formatCount(lineBunches(record)),
    },
    {
      title: 'Tallos / ramo',
      dataIndex: 'stems_per_bunch',
      key: 'stems_per_bunch',
      align: 'right',
      render: formatCount,
    },
    {
      title: 'Tallos',
      key: 'stems',
      align: 'right',
      render: (_, record) => formatCount(lineStems(record)),
    },
    ...priceColumns,
  ];
  const columns = [...baseColumns, ...extraColumns];

  const totalBunches = components.reduce((sum, c) => sum + lineBunches(c), 0);
  const totalStems = components.reduce((sum, c) => sum + lineStems(c), 0);
  const totalCents = components.filter(hasComponentPrice).reduce((sum, c) => sum + valueCents(c), 0);

  return (
    <Table
      size="small"
      columns={columns}
      dataSource={components}
      rowKey={(record) => record.component_id}
      pagination={false}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}><Text strong>Total línea</Text></Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right"><Text strong>{formatCount(totalBunches)}</Text></Table.Summary.Cell>
          <Table.Summary.Cell index={2} />
          <Table.Summary.Cell index={3} align="right"><Text strong>{formatCount(totalStems)}</Text></Table.Summary.Cell>
          {priced && (
            <>
              <Table.Summary.Cell index={4} />
              <Table.Summary.Cell index={5} align="right"><Text strong>{formatNumber(totalCents / 100)}</Text></Table.Summary.Cell>
            </>
          )}
          {extraColumns.map((column, i) => (
            <Table.Summary.Cell key={column.key} index={baseColumns.length + i} />
          ))}
        </Table.Summary.Row>
      )}
    />
  );
};
